import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { subDays, format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label, selectedTask }) => { // Added selectedTask prop
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const centralTime = toZonedTime(new Date(label), 'America/Chicago');
    
    return (
      <div className="custom-tooltip" style={{ backgroundColor: '#fff', padding: '10px', border: '1px solid #ccc' }}>
        <p className="label">{`Date: ${format(centralTime, 'MM/dd/yyyy HH:mm')}`}</p>
        <p className="intro">{`${selectedTask} Time: ${data.taskTime.toFixed(1)} mins`}</p> {/* Now dynamic */}
        <p className="desc">{`Job #: ${data.job}`}</p>
        <p className="desc">{`User: ${data.user}`}</p>
      </div>
    );
  }

  return null;
};

export default function TaskTrendDashboard({ partId, processOptions, apiBaseUrl }) {
  const [selectedTask, setSelectedTask] = useState('');
  const [taskData, setTaskData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([subDays(new Date(), 30), new Date()]);

  useEffect(() => {
    if (processOptions.length > 0) {
      setSelectedTask(processOptions[0]);
    }
  }, [processOptions]);

  useEffect(() => {
    const fetchTaskData = async () => {
      if (!partId || !selectedTask) return;

      setLoading(true);
      const [startDate, endDate] = dateRange;
      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');

      try {
        const res = await fetch(`${apiBaseUrl}/parts/${partId}/task_times?task_type=${selectedTask}&start_date=${formattedStartDate}&end_date=${formattedEndDate}`);
        if (!res.ok) throw new Error('Failed to fetch task time data');
        const data = await res.json();
        setTaskData(data);
      } catch (error) {
        console.error("Error fetching task time data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTaskData();
  }, [partId, selectedTask, dateRange, apiBaseUrl]);

  return (
    <div className="mt-4 p-3 border rounded">
      <h5>Task Time Trend</h5>
      <div className="row mb-3 align-items-end">
        <div className="col-md-3">
          <label htmlFor="task-select" className="form-label">Task Type</label>
          <select
            id="task-select"
            className="form-select"
            value={selectedTask}
            onChange={(e) => setSelectedTask(e.target.value)}
          >
            {processOptions.map(proc => (
              <option key={proc} value={proc}>{proc}</option>
            ))}
          </select>
        </div>
        <div className="col-md-3">
            <label htmlFor="start-date" className="form-label">Start Date</label>
            <DatePicker
                selected={dateRange[0]}
                onChange={(date) => setDateRange([date, dateRange[1]])}
                selectsStart
                startDate={dateRange[0]}
                endDate={dateRange[1]}
                className="form-control"
            />
        </div>
        <div className="col-md-3">
            <label htmlFor="end-date" className="form-label">End Date</label>
            <DatePicker
                selected={dateRange[1]}
                onChange={(date) => setDateRange([dateRange[0], date])}
                selectsEnd
                startDate={dateRange[0]}
                endDate={dateRange[1]}
                minDate={dateRange[0]}
                className="form-control"
            />
        </div>
      </div>

      {loading ? (
        <div>Loading chart...</div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={taskData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" tickFormatter={(tick) => format(new Date(tick), 'MM/dd')} />
            <YAxis label={{ value: 'Time (minutes)', angle: -90, position: 'insideLeft' }} />
            <Tooltip content={<CustomTooltip selectedTask={selectedTask} />} /> {/* Pass selectedTask as a prop */}
            <Legend />
            <Line type="monotone" dataKey="taskTime" stroke="#8884d8" name={`${selectedTask} Time`} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}